/**
 * @import { MapProps } from '@vis.gl/react-google-maps'
 * @import { ItineraryDay } from '@/features/itinerary-map/types/itinerary-day'
 * @import { Port } from '@/features/itinerary-map/types/port'
 */

import { AttributionControl } from '@/ui/attribution-control/attribution-control';
import { MapTypeControl } from '@/ui/map-type-control/map-type-control';
import { Panel } from '@/ui/panel/panel';
import { ZoomControl } from '@/ui/zoom-control/zoom-control';
import { Map } from '@vis.gl/react-google-maps';
import { useState } from 'react';
import { usePortInfoContext } from '../../context/port-info';
import { useBounds } from '../../hooks/use-bounds';
import { useDay } from '../../hooks/use-day';
import { useMapDays } from '../../hooks/use-map-days';
import { BackToOverviewControl } from '../back-to-overview-control/back-to-overview-control';
import { DayOverview } from '../day-overview/day-overview';
import { InformationControl } from '../information-control/information-control';
import { ItineraryOverview } from '../itinerary-overview/itinerary-overview';
import { Legend } from '../legend/legend';
import { PathLayer } from '../path-layer';
import { PortInfo } from '../port-info';
import { PortsLayer } from '../ports-layer';
import classes from './desktop-map.module.css';

/**
 * @typedef {object} Info
 * @property {ItineraryDay} day
 * @property {google.maps.marker.AdvancedMarkerElement} marker
 */

/**
 * @param {object} props
 * @param {MapProps} props.mapProps
 * @returns {React.JSX.Element}
 */
export function DesktopMap({ mapProps }) {
  const [isPanelOpen, setIsPanelOpen] = useState(true);
  const { portInfo, setPortInfo } = usePortInfoContext();
  const { getCurrentDayNumber, viewDay } = useDay();
  const days = useMapDays();

  useBounds();

  /** @type {(dayNumber: string) => void} */
  const handleSelectDay = (dayNumber) => {
    viewDay(dayNumber);
    setIsPanelOpen(true);
    setPortInfo(null);
  };

  /** @type {(index: number) => void} */
  const handleChangeSelectedDayIndex = (index) => {
    setPortInfo((prevState) => prevState && { ...prevState, selectedDayIndex: index });
  };

  /**
   * @param {ItineraryDay[] | undefined} days
   * @param {Port} port
   * @returns {ItineraryDay[]}
   */
  const getDaysAtPort = (days, port) => {
    const position = port?.position;
    if (!days || !position?.length) {
      return [];
    }

    return days.filter(
      (day) => day.port.position?.length && day.port.position.every((value, i) => value === position[i])
    );
  };

  /** @type {(day: ItineraryDay, marker: google.maps.marker.AdvancedMarkerElement) => void} */
  const handlePortClick = (day, marker) => {
    const daysAtPort = getDaysAtPort(days, day.port);
    setPortInfo(daysAtPort.length ? { days: daysAtPort, marker, selectedDayIndex: 0 } : null);
  };

  return (
    <div className={classes.desktopMap}>
      <div style={{ position: 'relative', display: 'flex', flex: 'auto' }}>
        <Map style={{ flex: 'auto' }} {...mapProps}>
          <PathLayer />
          <PortsLayer days={days} onPortClick={handlePortClick} />
          {portInfo?.days[0]?.port.position && (
            <PortInfo
              key={portInfo.days[0].port.position.join(',')}
              days={portInfo.days}
              selectedDayIndex={portInfo.selectedDayIndex}
              anchor={portInfo.marker}
              onClose={() => setPortInfo(null)}
              onChangeSelectedDayIndex={handleChangeSelectedDayIndex}
              onSelectDay={handleSelectDay}
            />
          )}
          <BackToOverviewControl />
          <InformationControl />
          <Legend days={days} />
          <AttributionControl />
          <MapTypeControl />
          <ZoomControl />
        </Map>
        <Panel label="Itinerary" isOpen={isPanelOpen} isOpenChange={setIsPanelOpen}>
          <ItineraryOverview />
          {getCurrentDayNumber() && <DayOverview />}
        </Panel>
      </div>
    </div>
  );
}
